import { Check, ChevronDown, Tag } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import type { CalendarFilterTag } from "./types"

type Props = {
  tags: CalendarFilterTag[]
  // The currently selected tag id, or null for "all tags".
  selected: number | null
  onSelect: (tagId: number | null) => void
}

// Single-select filter that narrows the calendar to pieces wearing one tag.
// Options are limited to tags on a piece scheduled today or later (computed
// server side), so an empty list means there's nothing left to filter by.
export function TagFilter({ tags, selected, onSelect }: Props) {
  const active = selected != null
  const selectedTag = active ? tags.find((t) => t.id === selected) : undefined
  const label = selectedTag ? selectedTag.name : "All tags"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          disabled={tags.length === 0}
          aria-label={label}
          className={cn(
            active &&
              "border-accent bg-accent-faded text-accent-display hover:bg-accent-faded",
          )}
        >
          <Tag className="h-4 w-4" />
          <span className="hidden max-w-[10rem] truncate sm:inline">{label}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuItem onSelect={() => onSelect(null)}>
          <Check className={cn("h-3.5 w-3.5", active && "invisible")} />
          <span>All tags</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {tags.map((t) => (
          <DropdownMenuItem key={t.id} onSelect={() => onSelect(t.id)}>
            <Check className={cn("h-3.5 w-3.5", selected !== t.id && "invisible")} />
            <span className="truncate">{t.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
